/**
 * Chart-of-accounts import — `/ledger/accounts/import`.
 *
 * Two calls, one act: preview, then apply what was previewed.
 *
 * The dry run answers a deterministic diff and a hash of it. The apply must
 * carry that hash back, so the thing applied is the thing an operator read. If
 * somebody creates an account in between, the hash stops matching and the
 * apply is refused — which is exactly what previewing is for, so the screen
 * says that when it happens rather than reporting a generic failure.
 *
 * The preview is shown whole: creates, updates with their changed fields, and
 * the accounts the import would leave alone. "Unchanged" is information — it
 * is how an operator sees that a chart they thought they were replacing mostly
 * already exists.
 *
 * A malformed line is reported with its number instead of being skipped. An
 * import that quietly drops a row is how a chart ends up missing an account
 * nobody notices until a statement will not balance.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, FileUp, PlayCircle } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@erp/ui";

import { newIdempotencyKey } from "@/features/shared/ops-api";
import { ApiError } from "@/lib/api/errors";

import {
  dryRunAccountImport,
  importAccounts,
  parseAccounts,
  type CoaImportConflictPolicy,
  type CoaImportDiff,
  type CoaImportResult,
} from "../coa-import-api";

const SAMPLE = `# code, name, type, normal balance, postable, parent code, currency
1000, Assets, asset, debit, false
1100, Cash and equivalents, asset, debit, false, 1000
1110, Cash on hand, asset, debit, true, 1100`;

export interface CoaImportPageProps {
  canPreview?: boolean;
  canApply?: boolean;
}

export function CoaImportPage({ canPreview = false, canApply = false }: CoaImportPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [policy, setPolicy] = useState<CoaImportConflictPolicy>("reject");
  const [diff, setDiff] = useState<CoaImportDiff | null>(null);
  const [result, setResult] = useState<CoaImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = parseAccounts(text);

  const preview = () => {
    setBusy(true);
    setError(null);
    setResult(null);
    void dryRunAccountImport(parsed.accounts, policy)
      .then(setDiff)
      .catch(() => setError(t("ledger.coaImport.previewError", "The preview could not run.")))
      .finally(() => setBusy(false));
  };

  const apply = () => {
    if (diff === null) return;
    setBusy(true);
    setError(null);
    void importAccounts(parsed.accounts, policy, diff.diff_hash, newIdempotencyKey())
      .then(async (applied) => {
        setResult(applied);
        setDiff(null);
        await queryClient.invalidateQueries({ queryKey: ["ledger"] });
      })
      .catch((caught: unknown) =>
        setError(
          caught instanceof ApiError && caught.status === 409
            ? t(
                "ledger.coaImport.stale",
                "The chart changed since this preview, so the import was refused. Preview again to see what it would do now.",
              )
            : t("ledger.coaImport.applyError", "The import could not be applied."),
        ),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("ledger.coaImport.title", "Import accounts")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "ledger.coaImport.subtitle",
            "Preview first, then apply exactly what you read. An import is refused if the chart moved in between.",
          )}
        </p>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("ledger.coaImport.inputTitle", "The accounts")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "ledger.coaImport.format",
              "One account per line: code, name, type, normal balance, postable, parent code, currency. A parent is named by its code, so a chart from elsewhere carries over unchanged.",
            )}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="coa-import-text">{t("ledger.coaImport.accounts", "Accounts")}</Label>
            <Textarea
              id="coa-import-text"
              dir="ltr"
              rows={10}
              className="font-mono text-xs"
              placeholder={SAMPLE}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setDiff(null);
              }}
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="coa-import-policy">
                {t("ledger.coaImport.policy", "If a code already exists")}
              </Label>
              <Select
                value={policy}
                onValueChange={(value) => {
                  setPolicy(value as CoaImportConflictPolicy);
                  setDiff(null);
                }}
              >
                <SelectTrigger id="coa-import-policy" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reject">
                    {t("ledger.coaImport.reject", "Refuse the whole import")}
                  </SelectItem>
                  <SelectItem value="update_metadata">
                    {t("ledger.coaImport.update", "Update its name and metadata")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canPreview ? (
              <Button
                type="button"
                disabled={busy || parsed.accounts.length === 0}
                onClick={preview}
              >
                <PlayCircle className="size-4" aria-hidden="true" />
                {t("ledger.coaImport.preview", "Preview {{count}} account(s)", {
                  count: parsed.accounts.length,
                })}
              </Button>
            ) : null}
          </div>

          {parsed.problems.length === 0 ? null : (
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm text-destructive">
                <CircleAlert className="size-4" aria-hidden="true" />
                {t(
                  "ledger.coaImport.badLines",
                  "{{count}} line(s) could not be read and are not part of the import.",
                  { count: parsed.problems.length },
                )}
              </p>
              <ul className="space-y-0.5 text-xs text-muted-foreground">
                {parsed.problems.map((problem) => (
                  <li key={problem.line} dir="ltr">
                    {t("ledger.coaImport.lineProblem", "line {{line}}: {{message}}", {
                      line: problem.line,
                      message: problem.message,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {result !== null ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm">
              {t(
                "ledger.coaImport.applied",
                "{{created}} account(s) created and {{updated}} updated, in one transaction.",
                { created: result.created_count, updated: result.updated_count },
              )}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {diff === null ? null : (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("ledger.coaImport.diffTitle", "What it would do")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant={diff.valid ? "success" : "destructive"}>
                {diff.valid
                  ? t("ledger.coaImport.usable", "This import can be applied")
                  : t("ledger.coaImport.blocked", "This import cannot be applied")}
              </Badge>
              <span className="text-muted-foreground">
                {t("ledger.coaImport.counts", "{{create}} to create · {{update}} to update", {
                  create: diff.changes.filter((change) => change.action === "create").length,
                  update: diff.changes.filter((change) => change.action === "update").length,
                })}
              </span>
            </div>

            {diff.issues.length === 0 ? null : (
              <ul className="space-y-1 text-sm">
                {diff.issues.map((issue, index) => (
                  <li
                    key={`${issue.code}-${issue.account_code ?? index}`}
                    className="rounded-md border p-2"
                  >
                    <span className="block">
                      {issue.account_code === null || issue.account_code === undefined ? null : (
                        <span dir="ltr" className="font-mono">
                          {issue.account_code}{" "}
                        </span>
                      )}
                      {issue.message}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="overflow-x-auto">
              <table className="w-full min-w-[36rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.coaImport.code", "Code")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.coaImport.action", "Action")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.coaImport.changes", "What changes")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diff.changes.map((change) => (
                    <tr key={change.code} className="border-b last:border-0">
                      <td className="p-2 font-mono" dir="ltr">
                        {change.code}
                      </td>
                      <td className="p-2">
                        {t(`ledger.coaAction.${change.action}`, change.action)}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {change.changed_fields === undefined || change.changed_fields.length === 0
                          ? change.action === "unchanged"
                            ? t("ledger.coaImport.nothing", "Nothing — it already matches")
                            : "—"
                          : change.changed_fields.join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {canApply ? (
              <Button type="button" disabled={busy || !diff.valid} onClick={apply}>
                <FileUp className="size-4" aria-hidden="true" />
                {t("ledger.coaImport.apply", "Apply this preview")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
